import * as Dialog from '@radix-ui/react-dialog'
import { ColumnDef, Row } from '@tanstack/react-table'
import { Dropdown } from 'components/form/inputs/Dropdown'
import { Button } from 'designSystem/components/Button'
import { useState } from 'react'
import styled from 'styled-components'
import { FormField } from 'types'

import { BulkActionModalEditConfiguration } from './BulkActionModalEditConfiguration'

const ModalOverlay = styled(Dialog.Overlay)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const ModalContent = styled(Dialog.Content)`
  background: ${({ theme }) => theme.forecast.cardBackground};
  border-radius: 0.5rem;
  padding: 1.5rem;
  width: fit-content;
  min-width: 32rem;
  max-width: 90vw;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
`

const ModalHeader = styled.div`
  margin-bottom: 1.5rem;
`

const ModalTitle = styled(Dialog.Title)`
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
`

const SelectedRowsInfo = styled.div`
  background-color: ${({ theme }) => theme.forecast.glassBackground};
  border-radius: 0.375rem;
  padding: 0.75rem;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.875rem;
`

const ActionSelectionContainer = styled.div`
  margin-bottom: 1.5rem;
`

const ActionSelectionLabel = styled.div`
  color: ${({ theme }) => theme.forecast.defaultText};
  font-weight: 500;
  margin-bottom: 0.75rem;
`

const ActionSelectionButton = styled(Button)<{
  $hasChevron: boolean
}>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 8rem;
`

const BulkEditFormContainer = styled.div`
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
`

const DeleteWarningContainer = styled.div`
  padding: 1rem;
  background-color: ${({ theme }) => theme.forecast.redGlassBackground};
  border-radius: 0.375rem;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.forecast.danger};
`

const getActionButtonText = (selectedAction: string): string => {
  switch (selectedAction) {
    case 'edit':
      return 'Update Rows'
    case 'delete':
      return 'Delete Rows'
    default:
      return 'Execute Action'
  }
}

interface BulkActionConfig<TData = unknown> {
  key: string
  label: string
  component?: React.ComponentType<BulkActionComponentProps<TData>>
  buttonProps?: {
    danger?: boolean
    ghost?: boolean
  }
}

interface BulkActionComponentProps<TData = unknown> {
  selectedRows: Row<TData>[]
  onClose: () => void
}

export interface BulkActionModalProps<TData = unknown> {
  isOpen: boolean
  onClose: () => void
  selectedRows: Row<TData>[]
  editableFields: {
    label: string
    value: string | FormField | ColumnDef<TData>
  }[]
  onBulkEdit: (
    selectedRows: Row<TData>[],
    fieldKey: string,
    newValue: string | number | string[] | undefined
  ) => void
  onBulkDelete?: (selectedRows: Row<TData>[]) => void
  customActions?: BulkActionConfig<TData>[]
}

export const BulkActionModal = <TData = unknown,>({
  isOpen,
  onClose,
  selectedRows,
  editableFields = [],
  onBulkEdit,
  onBulkDelete,
  customActions = [],
}: BulkActionModalProps<TData>) => {
  const [selectedAction, setSelectedAction] = useState<string>('edit')
  const [selectedFieldKey, setSelectedFieldKey] = useState<string>('')
  const [newFieldValue, setNewFieldValue] = useState<
    string | number | string[] | undefined
  >('')

  const availableActions = [
    { key: 'edit', label: 'Bulk Edit Field' },
    ...(onBulkDelete ? [{ key: 'delete', label: 'Bulk Delete' }] : []),
    ...customActions.map((action) => ({
      key: action.key,
      label: action.label,
    })),
  ]

  const hasMultipleActions = availableActions.length > 1
  const currentActionLabel =
    availableActions.find((action) => action.key === selectedAction)?.label ||
    'Select Action'

  const handleActionChange = (action: string) => {
    setSelectedAction(action)
    // Reset form when switching actions
    setSelectedFieldKey('')
    setNewFieldValue('')
  }

  const handleBulkAction = () => {
    if (selectedAction === 'delete' && onBulkDelete !== undefined) {
      onBulkDelete(selectedRows)
      onClose()
    } else if (
      selectedAction === 'edit' &&
      onBulkEdit &&
      selectedFieldKey &&
      newFieldValue
    ) {
      onBulkEdit(selectedRows, selectedFieldKey, newFieldValue)
      onClose()
    }
  }

  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={onClose}>
        <Dialog.Portal>
          <ModalOverlay>
            <ModalContent>
              <ModalHeader>
                <ModalTitle>Bulk Actions</ModalTitle>
              </ModalHeader>

              <SelectedRowsInfo>
                {selectedRows.length} row(s) selected
              </SelectedRowsInfo>

              <ActionSelectionContainer>
                <ActionSelectionLabel>Choose an action:</ActionSelectionLabel>
                {hasMultipleActions ? (
                  <Dropdown
                    options={availableActions.map((action) => ({
                      value: action.key,
                      label: action.label,
                    }))}
                    defaultValue={selectedAction}
                    onValueChange={handleActionChange}
                    placeholder='Select Action'
                  />
                ) : (
                  <ActionSelectionButton
                    $hasChevron={false}
                    variant='raised'
                    size='lg'
                    disabled>
                    {currentActionLabel}
                  </ActionSelectionButton>
                )}
              </ActionSelectionContainer>

              {selectedAction === 'edit' && (
                <BulkEditFormContainer>
                  <BulkActionModalEditConfiguration
                    editableFields={editableFields}
                    selectedFieldKey={selectedFieldKey}
                    setSelectedFieldKey={setSelectedFieldKey}
                    newFieldValue={newFieldValue}
                    setNewFieldValue={setNewFieldValue}
                  />
                </BulkEditFormContainer>
              )}

              {customActions.map((action) => {
                if (selectedAction === action.key && action.component) {
                  const ActionComponent = action.component
                  return (
                    <ActionComponent
                      key={action.key}
                      selectedRows={selectedRows}
                      onClose={onClose}
                    />
                  )
                }
                return null
              })}

              {selectedAction === 'delete' && onBulkDelete && (
                <DeleteWarningContainer>
                  ⚠️ This action will permanently delete the selected rows and
                  cannot be undone.
                </DeleteWarningContainer>
              )}

              {!customActions.some(
                (action) => action.key === selectedAction
              ) && (
                <ModalActions>
                  <Button
                    variant={
                      selectedAction === 'delete' ? 'destructive' : 'raised'
                    }
                    size='lg'
                    onClick={handleBulkAction}>
                    {getActionButtonText(selectedAction)}
                  </Button>
                </ModalActions>
              )}
            </ModalContent>
          </ModalOverlay>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  )
}
